<template>
  <div>
    <template v-if="resolved.type === 'category'">
      <category :category="resolved" />
    </template>
    <template v-else-if="resolved.type === 'product'">
      <product :product="resolved" />
    </template>
    <template v-else-if="resolved.type === 'color'">
      <color-page :resolve="resolved" />
    </template>
  </div>
</template>

<script>
import Category from '../components/category/Category'
import Product from '../components/product/Product'
import ColorPage from '../components/color/ColorPage'

export default {
  components: {
    Category,
    Product,
    ColorPage
  },
  async asyncData ({ app, route, error, redirect }) {
    try {
      let path = route.path

      const hasTrailingSlash = path.match(/\/$/)

      const colorPageMatch = path.match(/^\/couleur-(.*)$/)

      if (colorPageMatch && hasTrailingSlash) {
        path = path.slice(0, -1)
        return redirect(301, path)
      }

      if (colorPageMatch) {
        const { data } = await app.$api.get('/color-page', {
          params: {
            slug: colorPageMatch[1]
          }
        })

        return {
          resolved: {
            type: 'color',
            ...data
          }
        }
      }

      const filterMatch = path.match(/^(.*\/)caracteristiques\/(.*)$/)

      if (filterMatch) {
        path = filterMatch[1]
      }

      if (hasTrailingSlash) {
        path = path.slice(0, -1)
      }

      path = path.split('/')

      path.splice(0, 1)

      const { data } = await app.$api.post('/resolve', {
        path
      })

      if (data.resolved.type === 'category' && !hasTrailingSlash && !filterMatch) {
        return redirect(301, `/${path.join('/')}/`)
      }

      return data
    } catch (e) {
      if (e?.response?.status === 404) {
        // Redirections automatiques pour pagination blog (évite de surcharger le Google Sheet)
        const pathStr = route.path

        // Pagination tags blog -> page 1 du tag
        if (pathStr.match(/^\/blog\/tag\/([^/]+)\/page\/\d+\/?$/)) {
          const tag = pathStr.match(/^\/blog\/tag\/([^/]+)/)[1]
          return redirect(301, `/blog/tag/${tag}/`)
        }

        // Pagination catégories blog -> page 1 de la catégorie
        if (pathStr.match(/^\/blog\/category\/([^/]+)\/page\/\d+\/?$/)) {
          const cat = pathStr.match(/^\/blog\/category\/([^/]+)/)[1]
          return redirect(301, `/blog/category/${cat}/`)
        }

        // Pagination blog générale -> blog
        if (pathStr.match(/^\/blog\/page\/\d+\/?$/)) {
          return redirect(301, '/blog/')
        }

        // Redirections automatiques pour URLs panier avec paramètres
        if (pathStr.startsWith('/panier') && route.query && Object.keys(route.query).length > 0) {
          return redirect(301, '/panier/')
        }

        try {
          const path = route.path
          const redirection = await app.$api.post('/redirections', {
            path
          })
          if (redirection.data.redirect) {
            redirect(301, redirection.data.redirect)
            return
          }
        } catch (e) {
          app.$gtmEvents.onRuntimeError(e, 'resolve/redirection')
        }

        error({ statusCode: 404, message: e.response.data })
      } else {
        error({ statusCode: 500, message: e?.message })
      }
    }
  },
  head () {
    if (this.resolved.type === 'product') {
      const title = this.resolved.properties.metaName || this.resolved.label
      const description = this.resolved.properties.metaDescription
      const url = this.$host + this.$route.path
      const image = this.resolved.cover
      return {
        title,
        meta: [
          { hid: 'description', name: 'description', content: description },
          { hid: 'og:title', property: 'og:title', content: title },
          { hid: 'og:description', property: 'og:description', content: description },
          { hid: 'og:url', property: 'og:url', content: url },
          { hid: 'og:type', property: 'og:type', content: 'product' },
          { hid: 'og:image', property: 'og:image', content: image },
          { hid: 'og:site_name', property: 'og:site_name', content: 'SkyLantern' },
          { hid: 'product:price:amount', property: 'product:price:amount', content: this.resolved.pricing?.unit_taxed_price },
          { hid: 'product:price:currency', property: 'product:price:currency', content: 'EUR' }
        ],
        link: [
          {
            rel: 'canonical',
            href: url
          }
        ]
      }
    } else if (this.resolved.type === 'category') {
      let title = this.resolved.properties.meta_title || (this.resolved.label + ' - Skylantern.fr')
      let description = this.resolved.properties.meta_description || ''
      if (this.$route.query.page) {
        const page = parseInt(this.$route.query.page)
        title = title + ' - Page ' + page
        description = description + ' - Page ' + page
      }

      const meta = [
        {
          hid: 'description',
          name: 'description',
          content: description
        }
      ]

      if (this.$route.path.match(/^(.*)\/caracteristiques\/(.*)$/)) {
        meta.push({
          hid: 'robots',
          name: 'robots',
          content: 'noindex, nofollow'
        })
      }

      const url = this.$host + this.$helpers.makeUri(this.resolved.path, 'category') + (this.$route.query.page ? `?page=${this.$route.query.page}` : '')
      const image = this.resolved.properties?.cover

      meta.push(
        { hid: 'og:title', property: 'og:title', content: title },
        { hid: 'og:description', property: 'og:description', content: description },
        { hid: 'og:url', property: 'og:url', content: url },
        { hid: 'og:type', property: 'og:type', content: 'website' },
        { hid: 'og:site_name', property: 'og:site_name', content: 'SkyLantern' }
      )
      if (image) {
        meta.push({ hid: 'og:image', property: 'og:image', content: image })
      }

      return {
        title,
        meta,
        link: [
          {
            rel: 'canonical',
            href: url
          }
        ]
      }
    } else if (this.resolved.type === 'color') {
      const title = this.resolved.content.attributes.meta_title
      const description = this.resolved.content.attributes.meta_description
      return {
        title,
        meta: [
          { hid: 'description', name: 'description', content: description },
          { hid: 'og:title', property: 'og:title', content: title },
          { hid: 'og:description', property: 'og:description', content: description },
          { hid: 'og:type', property: 'og:type', content: 'website' },
          { hid: 'og:site_name', property: 'og:site_name', content: 'SkyLantern' }
        ]
      }
    }

    // else if (this.resolve?.__typename === 'ColorPage') {
    //   return {
    //     title: this.resolve.meta_title,
    //     meta: [
    //       {
    //         hid: 'description',
    //         name: 'description',
    //         content: this.resolve.meta_description
    //       }
    //     ]
    //   }
    // }
  }
}
</script>
